import { Filter } from "@/shared/lib/filter/filter";
import { providesList } from "@/services/backend/_lib/tag-helpers";
import { Lst } from "@/shared/lib/lst/lst";
import { backendApi } from "@/services/backend/api";
import { Address } from "@/services/backend/addresses/types/address";
import { Contact } from "@/services/backend/contacts/types/contact";
import { Representative } from "@/services/backend/representatives/types/representative";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import { Order } from "@/services/backend/vbs/orders/types/order";

const orderApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    vbsOrderPut: build.mutation<Order, PutRequest>({
      query: (request) => ({
        url: `/vbs/order`,
        method: "PUT",
        body: request,
      }),
      invalidatesTags: (result, _e, request) => [
        { type: "VbsOrder", id: request.id },
        { type: "VbsOrder", id: "LIST" },
        { type: "VbsTreatment", id: result?.treatmentId },
        { type: "VbsCalculation", id: result?.calculationId },
      ],
    }),
    vbsOrderShow: build.query<Order, ShowRequest>({
      query: (request) => ({
        url: `/vbs/order`,
        method: "GET",
        params: request,
      }),
      providesTags: (result) =>
        result ? [{ type: "VbsOrder", id: result.id }] : [],
    }),
    vbsOrderList: build.query<Lst<Order>, Partial<ListRequest>>({
      query: (request) => ({
        url: `/vbs/order/list`,
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) => providesList(result?.items, "VbsOrder"),
    }),
    vbsOrderUpdatePostalAddress: build.mutation<
      Order,
      UpdatePostalAddressRequest
    >({
      query: (request) => ({
        url: `/vbs/order/postal-address`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsOrder", id: request.id },
        "VbsOrder",
      ],
    }),
    vbsOrderUpdateServiceAddress: build.mutation<
      Order,
      UpdateServiceAddressRequest
    >({
      query: (request) => ({
        url: `/vbs/order/service-address`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsOrder", id: request.id },
        "VbsOrder",
      ],
    }),
    vbsOrderUpdateServiceContact: build.mutation<
      Order,
      UpdateServiceContactRequest
    >({
      query: (request) => ({
        url: `/vbs/order/service-contact`,
        method: "PATCH",
        body: {
          id: request.id,
          serviceContact: request.contact,
        },
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsOrder", id: request.id },
        "VbsOrder",
      ],
    }),
    vbsOrderUpdateRepresentative: build.mutation<
      Order,
      UpdateRepresentativeRequest
    >({
      query: (request) => ({
        url: `/vbs/order/representative`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsOrder", id: request.id },
        "VbsOrder",
      ],
    }),
    vbsOrderDelete: build.mutation({
      query: (request) => ({
        url: `vbs/order`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "VbsOrder", id: "LIST" },
        { type: "VbsOrder", id: request.id },
      ],
    }),
  }),
});

export const {
  useVbsOrderPutMutation,
  useVbsOrderShowQuery,
  useVbsOrderListQuery,
  useVbsOrderUpdatePostalAddressMutation,
  useVbsOrderUpdateServiceAddressMutation,
  useVbsOrderUpdateServiceContactMutation,
  useVbsOrderUpdateRepresentativeMutation,
  useVbsOrderDeleteMutation,
} = orderApi;

export interface PutRequest {
  id: string;
  processId: string | null;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
  offerId: string | null;
  date: string | null;
  channel: string | null;
  customerReference: string | null;
  internalComment: string | null;
}

export interface ShowRequest {
  id: string;
}

export interface ListRequest
  extends DocumentBaseFilters, DocumentCommonFilters {
  customerReference?: Filter<string>;
  orderBy?: OrdrBy<string>[];
  date?: Filter<string>;
  immutable?: Filter<boolean>;
  limit?: number;
  offset?: number;
}

export interface UpdatePostalAddressRequest {
  id: string;
  postalAddress: Address;
}

export interface UpdateServiceAddressRequest {
  id: string;
  serviceAddress: Address;
}

export interface UpdateServiceContactRequest {
  id: string;
  contact: Contact;
}

export interface UpdateRepresentativeRequest {
  id: string;
  representative: Representative;
}
